import type { PlayerState } from "../../model/PlayerState.js";
import type { Roll } from "../../model/dice/Roll.js";
import { ActionGameState } from "./ActionGameState.js";
import type { Board } from "../../model/Board.js";
import type { PlayerType } from "../../model/PlayerType.js";
import { Move } from "../../model/Move.js";
import type { DescribePlayerNames } from "./GameState.js";
import { getDescribePlayerName } from "./GameState.js";
import { isSameRuntimeType } from "../../../util/tsutils.js";


/**
 * A game state that represents a roll that was made in a game.
 */
export class RolledGameState extends ActionGameState {
    private readonly roll: Roll;
    private readonly availableMoves: Move[];

    constructor(
        board: Board,
        lightPlayer: PlayerState,
        darkPlayer: PlayerState,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
        availableMoves: Move[],
    ) {
        super(board, lightPlayer, darkPlayer, timeSinceGameStartMs, turn);
        this.roll = roll;
        this.availableMoves = availableMoves;
    }

    getRoll(): Roll {
        return this.roll;
    }

    getAvailableMoves(): Move[] {
        return this.availableMoves;
    }

    override describe(playerNames: DescribePlayerNames): string {
        const playerName = getDescribePlayerName(this.getTurn(), playerNames);

        let noMoves = "";
        if (this.availableMoves.length === 0) {
            if (this.roll.value() === 0) {
                noMoves = ", and had no moves";
            } else {
                noMoves = ", and all moves were blocked";
            }
        }
        return `${playerName} rolled ${this.roll.value()}${noMoves}`;
    }

    override equals(other: unknown): boolean {
        if (other === this)
            return true;
        if (!isSameRuntimeType(this, other))
            return false;

        return ActionGameState._baselineEquals(this, other)
            && this.roll.equals(other.roll)
            && Move.setEquals(this.availableMoves, other.availableMoves);
    }
}
